import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button } from 'antd';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { PieChart } from '@mui/x-charts/PieChart';
import { BarChart } from '@mui/x-charts/BarChart';
import { ScatterChart } from '@mui/x-charts/ScatterChart';
import { ThemeContext } from '../../../context/ThemeContext';
import { chartAxisSx, mpdCardSx } from './portfolioMuiTheme';
import {
  MATURITY_BUCKET_ORDER,
  STRATEGY_CHART_COLORS,
  portfolioTypeToStrategy,
  yearsToMaturity,
} from './portfolioAnalyticsUtils';

const ISSUER_PREVIEW_COUNT = 6;

function ResponsiveChartBox({ height, children, fill = false }) {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const measure = () => {
      const rect = node.getBoundingClientRect();
      const nextWidth = Math.floor(rect.width);
      const nextHeight = Math.floor(rect.height);
      if (nextWidth > 0 && nextHeight > 0) {
        setSize({ width: nextWidth, height: nextHeight });
      }
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  if (fill) {
    return (
      <Box
        ref={ref}
        sx={{
          flex: '1 1 auto',
          minHeight: height,
          width: '100%',
          minWidth: 0,
          position: 'relative',
        }}
      >
        {size.width > 0 && size.height > 0 && (
          <Box sx={{ position: 'absolute', inset: 0 }}>
            {children(size.width, size.height)}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      ref={ref}
      sx={{
        width: '100%',
        minWidth: 0,
        height,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {size.width > 0 && children(size.width, height)}
    </Box>
  );
}

function ChartShell({
  title,
  hint,
  chartHeight = 228,
  footer,
  bare = false,
  fill = false,
  children,
}) {
  const chartArea = bare ? (
    children
  ) : (
    <ResponsiveChartBox height={chartHeight} fill={fill}>
      {children}
    </ResponsiveChartBox>
  );

  return (
    <Paper
      elevation={0}
      className={`mpd-mui-chart${fill ? ' mpd-mui-chart--fill' : ''}`}
      sx={(theme) => ({
        ...mpdCardSx(theme),
        ...(fill ? { px: 1.75, pt: 1.75, pb: 1.5 } : {}),
        display: 'flex',
        flexDirection: 'column',
        height: fill ? '100%' : 'auto',
        minHeight: fill ? chartHeight + 72 : undefined,
        minWidth: 0,
        overflow: 'hidden',
      })}
    >
      <Typography
        variant="overline"
        sx={{
          color: 'text.secondary',
          fontWeight: 700,
          letterSpacing: '0.06em',
          display: 'block',
          mb: fill ? 1 : 0,
        }}
      >
        {title}
      </Typography>
      {hint && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: fill ? 0.5 : 0.75 }}>
          {hint}
        </Typography>
      )}
      {bare ? (
        <Box
          sx={{
            flex: fill ? '1 1 0' : 'none',
            minHeight: fill ? chartHeight : undefined,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {chartArea}
        </Box>
      ) : (
        <Box
          sx={{
            flex: fill ? '1 1 0' : 'none',
            minHeight: chartHeight,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {chartArea}
        </Box>
      )}
      {footer && (
        <Box sx={{ mt: 1.25 }}>
          {footer}
        </Box>
      )}
    </Paper>
  );
}

function ViewAllModalLink({ totalCount, previewCount, noun, modalTitle, children }) {
  const [open, setOpen] = useState(false);

  if (totalCount <= previewCount) return null;

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button type="link" onClick={() => setOpen(true)} style={{ fontWeight: 600, padding: 0 }}>
          {`View all (${totalCount} ${noun})`}
        </Button>
      </Box>
      <Modal
        title={modalTitle}
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={760}
        destroyOnClose
        styles={{ body: { maxHeight: '72vh', overflowY: 'auto', paddingTop: 8 } }}
      >
        {open ? children : null}
      </Modal>
    </>
  );
}

function SharedStrategyLegend({ entries }) {
  if (!entries.length) return null;

  return (
    <Paper
      elevation={0}
      sx={(theme) => ({
        ...mpdCardSx(theme),
        p: 1.5,
        gridColumn: '1 / -1',
      })}
    >
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 1 }}
      >
        Strategy colors — used in allocation and yield vs maturity charts
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1, sm: 1.5 } }}>
        {entries.map((s) => (
          <Box
            key={s.type}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              fontSize: 12,
              color: 'text.secondary',
              pr: 1,
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                flexShrink: 0,
                bgcolor: STRATEGY_CHART_COLORS[s.strategy?.id] || '#868685',
              }}
            />
            <span>
              {s.strategy?.title || s.type}
              {s.weightPct > 0 && (
                <Typography component="span" variant="caption" sx={{ color: 'text.disabled', ml: 0.5 }}>
                  ({s.weightPct.toFixed(1)}%)
                </Typography>
              )}
            </span>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

function issuerBarScaleMax(maxIssuerWeight) {
  return Math.max(30, Math.ceil(maxIssuerWeight / 5) * 5 + 5);
}

function IssuerConcentrationRows({ issuers, maxIssuerWeight, fill = false }) {
  const scaleMax = issuerBarScaleMax(maxIssuerWeight);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: fill ? 0 : 1.25,
        justifyContent: fill ? 'space-between' : 'flex-start',
        flex: fill ? 1 : 'none',
        minHeight: 0,
        width: '100%',
        py: fill ? 0.75 : 0,
        px: fill ? 0.25 : 0,
      }}
    >
      {issuers.map((item) => (
        <Box
          key={item.issuer}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 42%) minmax(0, 1fr) auto',
            alignItems: 'center',
            gap: 1.25,
            minWidth: 0,
          }}
        >
          <Typography
            component="div"
            variant="caption"
            title={item.issuer}
            sx={{
              fontSize: 11,
              lineHeight: 1.35,
              fontWeight: 500,
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              wordBreak: 'break-word',
            }}
          >
            {item.issuer}
          </Typography>
          <Box
            sx={{
              height: 10,
              borderRadius: 1.25,
              bgcolor: 'action.hover',
              overflow: 'hidden',
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: `${Math.min(100, (item.weightPct / scaleMax) * 100)}%`,
                height: '100%',
                borderRadius: 1.25,
                bgcolor: '#9fe870',
                minWidth: item.weightPct > 0 ? 3 : 0,
              }}
            />
          </Box>
          <Typography
            variant="caption"
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: 'text.secondary',
              width: 42,
              textAlign: 'right',
              flexShrink: 0,
            }}
          >
            {item.weightPct.toFixed(1)}%
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function PortfolioAnalyticsCharts({ analytics, holdings }) {
  const { isDark } = useContext(ThemeContext);
  const axisSx = chartAxisSx(isDark);

  const strategyEntries = useMemo(
    () => analytics.strategyBreakdown.filter((s) => s.marketValue > 0),
    [analytics.strategyBreakdown]
  );

  const pieData = useMemo(
    () => strategyEntries.map((s, i) => ({
      id: i,
      value: s.marketValue,
      label: s.strategy?.title || s.type,
      color: STRATEGY_CHART_COLORS[s.strategy?.id] || '#868685',
    })),
    [strategyEntries]
  );

  const ladderLabels = MATURITY_BUCKET_ORDER.filter((b) => b !== 'Unknown');
  const ladderValues = ladderLabels.map((label) => analytics.maturityPct[label] || 0);

  const scatterSeries = useMemo(() => {
    const byStrategy = {};
    (holdings || []).forEach((h, idx) => {
      const strategy = portfolioTypeToStrategy(h.type);
      const key = strategy?.id || 'other';
      if (!byStrategy[key]) {
        byStrategy[key] = {
          label: strategy?.title || 'Other',
          color: STRATEGY_CHART_COLORS[key] || '#888',
          data: [],
        };
      }
      byStrategy[key].data.push({
        x: yearsToMaturity(h.maturityDate) ?? 0,
        y: Number(h.yield) || Number(h.coupon) || 0,
        id: idx,
      });
    });
    return Object.values(byStrategy);
  }, [holdings]);

  const allIssuers = analytics.allIssuers || [];
  const previewIssuers = allIssuers.slice(0, ISSUER_PREVIEW_COUNT);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
        gap: 2,
        minWidth: 0,
        alignItems: 'stretch',
      }}
    >
      <SharedStrategyLegend entries={strategyEntries} />

      <ChartShell
        title="Allocation by strategy"
        chartHeight={210}
      >
        {(width) => (
          <Box sx={{ position: 'relative', width, height: 210 }}>
            <PieChart
              width={width}
              height={210}
              series={[{
                innerRadius: '58%',
                outerRadius: '88%',
                paddingAngle: 2,
                cornerRadius: 4,
                data: pieData,
                highlightScope: { fade: 'global', highlight: 'item' },
              }]}
              hideLegend
              margin={{ top: 4, bottom: 4, left: 4, right: 4 }}
              sx={axisSx}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1 }}>
                {analytics.avgYield != null ? `${Number(analytics.avgYield).toFixed(2)}%` : '—'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                avg yield
              </Typography>
            </Box>
          </Box>
        )}
      </ChartShell>

      <ChartShell title="Maturity ladder" hint="Share of value by time to maturity" chartHeight={228}>
        {(width) => (
          <BarChart
            width={width}
            height={228}
            xAxis={[{
              scaleType: 'band',
              data: ladderLabels,
              tickLabelStyle: { fontSize: 10 },
            }]}
            yAxis={[{
              max: 100,
              tickLabelStyle: { fontSize: 10 },
              valueFormatter: (v) => `${v}%`,
            }]}
            series={[{
              data: ladderValues,
              color: '#378ADD',
              label: 'Weight',
              valueFormatter: (v) => `${Number(v).toFixed(1)}%`,
            }]}
            margin={{ left: 40, right: 8, top: 8, bottom: 32 }}
            grid={{ horizontal: true }}
            borderRadius={6}
            sx={axisSx}
          />
        )}
      </ChartShell>

      <Box
        sx={{
          gridColumn: '1 / -1',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 2,
          alignItems: 'stretch',
          minWidth: 0,
        }}
      >
        <ChartShell
          title="Yield vs maturity"
          hint="Each point is a holding"
          chartHeight={228}
          fill
        >
          {(width, chartH) => (
            <ScatterChart
              width={width}
              height={chartH}
              margin={{ left: 12, right: 12, top: 10, bottom: 10 }}
              xAxis={[{
                label: 'Years to maturity',
                height: 34,
                tickLabelStyle: { fontSize: 10 },
                labelStyle: { fontSize: 10 },
              }]}
              yAxis={[{
                label: 'Yield (%)',
                width: 52,
                tickLabelStyle: { fontSize: 10 },
                labelStyle: { fontSize: 10 },
                valueFormatter: (v) => `${v}%`,
              }]}
              series={scatterSeries.map((s) => ({
                label: s.label,
                data: s.data,
                color: s.color,
                markerSize: 7,
              }))}
              hideLegend
              grid={{ horizontal: true, vertical: true }}
              sx={axisSx}
            />
          )}
        </ChartShell>

        <ChartShell
          title="Issuer concentration"
          bare
          fill
          chartHeight={228}
          footer={(
            <ViewAllModalLink
              totalCount={allIssuers.length}
              previewCount={ISSUER_PREVIEW_COUNT}
              noun="issuers"
              modalTitle="Issuer concentration — all issuers"
            >
              <IssuerConcentrationRows
                issuers={allIssuers}
                maxIssuerWeight={analytics.maxIssuerWeight}
              />
            </ViewAllModalLink>
          )}
        >
          <IssuerConcentrationRows
            issuers={previewIssuers}
            maxIssuerWeight={analytics.maxIssuerWeight}
            fill
          />
        </ChartShell>
      </Box>
    </Box>
  );
}

export default PortfolioAnalyticsCharts;
